/**
 * Shared draft-state helpers for the Contact Details profile (ATAI-112 / S6).
 *
 * These mirror the mapping the legacy `ContactEditor` does when it hydrates a
 * contact from the API. They are duplicated here rather than lifted out of
 * `ContactEditor` on purpose: the legacy editor is a separate screen and this
 * slice must not change its behaviour.
 */

const normalizePhone = (phone = '') => (phone || '').replace(/\D/g, '');

export const mergePhonesWithAppendedPhones = (phones = [], appendedPhones = []) => {
  if (!Array.isArray(phones)) return [];

  const appendedByPhone = {};

  if (Array.isArray(appendedPhones)) {
    appendedPhones.forEach((ap) => {
      const key = normalizePhone(ap.phone);
      if (!key || appendedByPhone[key]) return;
      appendedByPhone[key] = ap;
    });
  }

  return phones.map((p, seq) => {
    const key = normalizePhone(p.phone);
    const appended = key && appendedByPhone[key];

    return {
      ...p,
      seq,
      publicDNC: appended ? Boolean(appended.publicDNC) : false,
    };
  });
};

export const blankContact = () => ({
  phones: [],
  emails: [],
});

/** Immutable Map (from the store) or plain object -> plain draft object. */
export const toDraft = (source) => {
  if (!source) return blankContact();

  const raw = typeof source.toJS === 'function' ? source.toJS() : source;

  const phones = raw.phones || [];
  const emails = raw.emails || [];
  const appendedPhones = raw.appendedPhones || [];

  return {
    ...raw,
    phones: mergePhonesWithAppendedPhones(phones, appendedPhones),
    emails: emails.map((e, seq) => ({ ...e, seq })),
    // the API echoes display-only `type`/`status` labels alongside the ids;
    // drop them so only typeId/statusId round-trip, as the legacy editor does.
    type: undefined,
    status: undefined,
  };
};

export const hasContactInformation = (contact = {}) =>
  Boolean((contact.phones && contact.phones.length) || (contact.emails && contact.emails.length));

export const hasIdentity = (contact = {}) =>
  Boolean(contact.firstName || contact.lastName || contact.companyName);

export const displayName = (contact = {}) =>
  [contact.firstName, contact.middleName, contact.lastName].filter(Boolean).join(' ') ||
  contact.name ||
  contact.companyName ||
  '';

/** The existing save endpoint rejects a contact without both of these. */
export const isPersistable = (contact = {}) => Boolean(contact.typeId) && Boolean(contact.statusId);
